import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { Badge } from '../../shared/ui'
import { useModalOpen } from '../../shared/ui/overlayStack'
import { LogoAvatar, type LogoAvatarState } from './avatar/LogoAvatar'
import { useBotStore } from './botStore'
import { CHROME_SPRING } from './botMotion'

// Floating round launcher for the Daten-Bot widget. Panel chrome (BotPanel)
// sits at z-[9997]; toasts own z-[9999] (ToastContainer). See DESIGN.md §14 —
// this is chrome, so it stays Liquid Glass via the `liquid-glass-pill` class
// (GlassPill itself renders a <div>; a launcher needs real <button>
// semantics for keyboard/AT users, so the pill's own utility classes are
// applied directly to a <button> rather than wrapping <GlassPill> in one).
//
// On desktop the panel's bottom-right corner anchors at the same point as
// this button (both `bottom-24 right-6`), so the launcher unmounts while
// the panel is open instead of sitting on top of it — otherwise its 56px
// hit-box would cover the composer's Send button underneath. The panel's
// own close button (+ Escape) takes over closing while it's open.
//
// Fixed in the bottom-right corner, not draggable (owner ask 2026-10-06:
// a dragged-and-remembered position left it floating over page content).
export function BotLauncher() {
  const { t } = useTranslation('bot')
  const reduced = useReducedMotion()
  const isOpen = useBotStore((s) => s.isOpen)
  // DESIGN.md §7 "No overlapping elements" — this launcher otherwise floats
  // over an open Modal's content (e.g. the M26 Assistent), so it hides
  // itself while any Modal is open.
  const modalOpen = useModalOpen()
  const toggle = useBotStore((s) => s.toggle)
  const pendingCount = useBotStore((s) => s.pendingTurnIds.length)
  const unseenCount = useBotStore((s) => s.unseenCount)

  // 'done' pulse: briefly true right after unseenCount increases, so an
  // answer arriving while the panel is closed gets one bounce.
  const [justAnswered, setJustAnswered] = useState(false)
  const prevUnseen = useRef(unseenCount)

  useEffect(() => {
    const increased = unseenCount > prevUnseen.current
    prevUnseen.current = unseenCount
    if (!increased) return
    setJustAnswered(true)
    const timer = setTimeout(() => setJustAnswered(false), 1200)
    return () => clearTimeout(timer)
  }, [unseenCount])

  const avatarState: LogoAvatarState = pendingCount > 0 ? 'thinking' : justAnswered ? 'done' : 'idle'
  // Unread count folds into the accessible name (not just the visual badge)
  // so it reaches screen-reader users too.
  const label = isOpen
    ? t('launcher.close')
    : unseenCount > 0
      ? t('launcher.openWithUnread', { count: unseenCount })
      : t('launcher.open')
  const badgeLabel = unseenCount > 99 ? '99+' : String(unseenCount)

  return (
    <AnimatePresence>
      {!isOpen && !modalOpen && (
        <motion.button
          id="bot-launcher-button"
          type="button"
          onClick={toggle}
          aria-label={label}
          // No aria-expanded: this button only exists in the DOM while
          // isOpen is false (see the file comment above), so the attribute
          // could only ever read "false" — dead state that never reflects
          // the panel actually being open. The panel's own
          // role="dialog"/aria-labelledby (BotPanel.tsx) conveys open state
          // instead.
          // NOTE: never add `relative` here — Tailwind emits it after `fixed`, so it would win
          // and drop the button into RootLayout's flex flow (off-screen). `fixed` already
          // positions the absolute unread badge. Caught in prod 2026-08-28.
          className="fixed bottom-[calc(6rem+var(--qm-bar,0px))] right-6 z-[9998] liquid-glass-pill rounded-full w-14 h-14 flex items-center justify-center text-on-surface motion-safe:transition-transform motion-safe:hover:scale-105 motion-safe:active:scale-95"
          initial={reduced ? false : { opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          // Exit is instant (duration: 0), not the entrance SPRING: this
          // button sits at a higher z-index than the panel (9998 vs 9997)
          // so it stays on top of — and pointer-interactive over — the
          // panel's own bottom-right corner (composer/Send button) for as
          // long as it's still animating out. An animated exit would let a
          // click during that window land on the stale launcher (re-closing
          // the panel that just opened) instead of the composer underneath.
          exit={{ opacity: 0, scale: 0.85, transition: { duration: 0 } }}
          transition={reduced ? { duration: 0 } : CHROME_SPRING}
        >
          <LogoAvatar state={avatarState} size={32} />
          {unseenCount > 0 && (
            <span className="absolute -top-1 -right-1" aria-hidden="true">
              <Badge tone="danger" size="sm">{badgeLabel}</Badge>
            </span>
          )}
        </motion.button>
      )}
    </AnimatePresence>
  )
}
